(() => {
  if (window.TabbedBestSellingProduct) return;

  class TabbedBestSellingProduct {
    constructor(root) {
      this.root = root;
      this.tabs = [...root.querySelectorAll('[role="tab"]')];
      this.panels = [...root.querySelectorAll('[data-tbsp-panel]')];
      this.checkoutReturnCallbacks = [];
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(index));
        tab.addEventListener('keydown', event => {
          const last = this.tabs.length - 1;
          let target = null;
          if (event.key === 'ArrowRight') target = index === last ? 0 : index + 1;
          if (event.key === 'ArrowLeft') target = index === 0 ? last : index - 1;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = last;
          if (target === null) return;
          event.preventDefault();
          this.activate(target);
          this.tabs[target].focus();
        });
      });
      this.panels.forEach(panel => this.initPanel(panel));
    }

    activeIndex() {
      return Math.max(0, this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true'));
    }

    activate(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.panels[i].hidden = !active;
        if (!active) this.pauseMedia(this.panels[i]);
      });
      this.tabs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    goToCheckout(url) {
      window.location.assign(url);
    }

    restoreCheckoutButtons() {
      this.checkoutReturnCallbacks.forEach(restore => restore());
    }

    pauseMedia(panel) {
      panel.querySelectorAll('video').forEach(video => video.pause());
      panel.querySelectorAll('iframe').forEach(frame => {
        if (frame.src) frame.src = frame.src;
      });
    }

    showMedia(panel, id) {
      const media = [...panel.querySelectorAll('[data-tbsp-media]')];
      if (!media.length) return;
      const target = media.find(item => item.dataset.tbspMedia === String(id)) || media[0];
      media.forEach(item => {
        if (item !== target) this.pauseMedia(item);
        item.hidden = item !== target;
      });
      panel.querySelectorAll('[data-tbsp-thumb]').forEach(thumb => {
        const active = thumb.dataset.tbspThumb === target.dataset.tbspMedia;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
        if (active) {
          const strip = thumb.parentElement;
          strip.scrollLeft = Math.max(0, thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2);
        }
      });
    }

    formatMoney(cents) {
      const format = this.root.dataset.tbspMoneyFormat || '$' + '{{ amount }}';
      // Use the shop's format even before Alpine's helper store has initialized.
      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_space_separator: [0, ' ', ','],
        amount_with_space_separator: [2, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.']
      };
      const formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, token) => {
        const [precision, thousands, decimal] = formats[token] || formats.amount;
        const [whole, fraction] = (Number(cents) / 100).toFixed(precision).split('.');
        return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (fraction ? decimal + fraction : '');
      });
      // Money formats may contain markup/entities; price nodes contain plain text.
      const template = document.createElement('template');
      template.innerHTML = formatted;
      return template.content.textContent;
    }

    initPanel(panel) {
      const info = panel.querySelector('.tbsp__info');
      const variants = JSON.parse(panel.querySelector('[data-tbsp-variants]').textContent);
      const optionGroups = [...panel.querySelectorAll('[data-tbsp-option]')];
      const form = panel.querySelector('.tbsp__form');
      const selectedOptions = () => optionGroups.map(group => group.querySelector('input:checked')?.value);
      const idInput = panel.querySelector('[data-tbsp-variant-id]');
      const findVariant = () => optionGroups.length
        ? variants.find(variant => variant.options.every((value, index) => value === selectedOptions()[index]))
        : variants.find(variant => String(variant.id) === idInput.value);
      const selectedPlan = () => panel.querySelector('[data-tbsp-plans] input[type="radio"]:checked')?.value || '';
      const planInput = panel.querySelector('[data-tbsp-selling-plan]');
      const price = panel.querySelector('[data-tbsp-price]');
      const totalLabel = panel.querySelector('[data-tbsp-total-label]');
      const compare = panel.querySelector('[data-tbsp-compare]');
      const priceSavings = panel.querySelector('[data-tbsp-price-savings]');
      const saleBadge = panel.querySelector('[data-tbsp-sale-badge]');
      const salePercent = panel.querySelector('[data-tbsp-sale-percent]');
      const add = panel.querySelector('[data-tbsp-add]');
      const addLabel = panel.querySelector('[data-tbsp-add-label]');
      const originalAddLabel = add.dataset.addLabel;
      const soldOutLabel = add.dataset.soldOutLabel;
      const buy = panel.querySelector('[data-tbsp-buy]');
      const buyLabel = panel.querySelector('[data-tbsp-buy-label]');
      const errorText = panel.querySelector('[x-ref="error_message"]');
      const quantity = panel.querySelector('[data-tbsp-quantity]');
      const minus = panel.querySelector('[data-tbsp-qty-minus]');
      const plus = panel.querySelector('[data-tbsp-qty-plus]');
      let initialRender = true;
      let lastVariantId = null;
      let canBuy = false;
      let addPending = false;
      let buyPending = false;
      let awaitingCheckoutReturn = false;
      let currentAvailable = false;

      const cartState = () => window.Alpine?.$data(info);
      const syncButtons = () => {
        const busy = addPending || buyPending || Boolean(cartState()?.loading);
        add.disabled = !canBuy || busy;
        if (buy) buy.disabled = !canBuy || busy;
        add.setAttribute('aria-busy', String(addPending));
        buy?.setAttribute('aria-busy', String(buyPending));
        addLabel.textContent = addPending ? add.dataset.loadingLabel : currentAvailable ? originalAddLabel : soldOutLabel;
        if (buyLabel) buyLabel.textContent = buyPending ? buy.dataset.loadingLabel : 'Buy Now';
      };
      const updateTooltip = (badge, values) => {
        if (!badge?.dataset.tbspTooltipTemplate) return;
        const resolved = badge.dataset.tbspTooltipTemplate.replace(/\{(product|tab|price|sale_percent|sale_amount)\}/gi, (_, key) => values[key.toLowerCase()] || '');
        badge.dataset.tooltip = resolved;
        if (badge.tip) badge.tip.textContent = resolved;
      };
      const showError = message => {
        if (errorText) errorText.textContent = message;
        const state = cartState();
        if (state) state.errorMessage = true;
      };

      const normalizeQuantity = variant => {
        if (!variant) return 1;
        const min = Number(variant.min) || 1;
        const increment = Number(variant.increment) || 1;
        const max = variant.max == null ? Infinity : Number(variant.max);
        quantity.min = min;
        quantity.step = increment;
        if (variant.max == null) quantity.removeAttribute('max');
        else quantity.max = max;
        const raw = quantity.type === 'hidden' ? min : Number(quantity.value);
        const bounded = Math.min(max, Math.max(min, Number.isFinite(raw) ? raw : min));
        const value = min + Math.floor((bounded - min) / increment) * increment;
        quantity.value = value;
        if (minus) minus.disabled = value <= min;
        if (plus) plus.disabled = value + increment > max;
        return value;
      };

      const render = () => {
        const variant = findVariant();
        const count = normalizeQuantity(variant);
        const currentPlan = selectedPlan();
        const availablePlans = variant?.plans || [];
        const oneTime = panel.querySelector('[data-tbsp-one-time]');
        if (oneTime) {
          oneTime.hidden = Boolean(variant?.requires_selling_plan);
          oneTime.querySelector('input').disabled = !variant || Boolean(variant.requires_selling_plan);
          oneTime.querySelector('[data-tbsp-one-time-price]').textContent = variant ? this.formatMoney(variant.price) : '';
        }
        panel.querySelectorAll('[data-tbsp-plan]').forEach(row => {
          const allocation = availablePlans.find(plan => String(plan.id) === row.dataset.tbspPlan);
          row.hidden = !allocation;
          row.querySelector('input').disabled = !allocation;
          row.querySelector('[data-tbsp-plan-price]').textContent = allocation ? this.formatMoney(allocation.price) : '';
        });
        const planValid = currentPlan === ''
          ? !variant?.requires_selling_plan && (!oneTime || oneTime.querySelector('input').checked)
          : availablePlans.some(plan => String(plan.id) === currentPlan);
        const preferSubscription = initialRender && this.root.dataset.tbspPreselectSubscription === 'true';
        if (!planValid || preferSubscription) {
          const firstPlan = panel.querySelector('[data-tbsp-plan]:not([hidden]) input');
          const optionalOneTime = oneTime && !oneTime.querySelector('input').disabled ? oneTime.querySelector('input') : null;
          const fallback = variant?.requires_selling_plan || currentPlan || preferSubscription ? firstPlan || optionalOneTime : optionalOneTime;
          panel.querySelectorAll('[data-tbsp-plans] input[type="radio"]').forEach(input => { input.checked = input === fallback; });
        }
        initialRender = false;
        const activePlan = selectedPlan();
        const allocation = availablePlans.find(plan => String(plan.id) === activePlan);
        if (planInput) {
          planInput.value = allocation?.id || '';
          planInput.disabled = !allocation;
        }
        const payablePrice = allocation?.price ?? variant?.price;
        const hasSavings = Boolean(variant && variant.compare_at_price > 0 && payablePrice >= 0 && variant.compare_at_price > payablePrice);
        price.textContent = variant ? this.formatMoney(payablePrice * count) : '';
        compare.textContent = variant ? this.formatMoney((variant.compare_at_price || 0) * count) : '';
        if (totalLabel) totalLabel.hidden = !variant || count <= 1;
        const percent = hasSavings ? Math.max(1, Math.round((variant.compare_at_price - payablePrice) * 100 / variant.compare_at_price)) : 0;
        const amount = hasSavings ? this.formatMoney((variant.compare_at_price - payablePrice) * count) : '';
        compare.hidden = !hasSavings;
        const tooltipValues = {
          product: info.dataset.tbspProduct || '',
          tab: info.dataset.tbspTabLabel || '',
          price: price.textContent,
          sale_percent: hasSavings ? percent + '%' : '',
          sale_amount: amount
        };
        panel.querySelectorAll('[data-tbsp-tooltip-template]').forEach(badge => updateTooltip(badge, tooltipValues));
        if (priceSavings) {
          priceSavings.hidden = !hasSavings;
          priceSavings.textContent = hasSavings ? 'Save ' + (priceSavings.dataset.format === 'amount' ? amount : percent + '%') : '';
        }
        if (saleBadge) {
          saleBadge.hidden = !hasSavings;
          if (salePercent) salePercent.textContent = hasSavings ? percent + '% off' : '';
          if (!hasSavings) saleBadge.close?.();
        }
        canBuy = Boolean(variant?.available && (!variant.requires_selling_plan || allocation));
        currentAvailable = Boolean(variant?.available);
        syncButtons();
        idInput.value = variant?.id || '';
        idInput.disabled = !variant;
        if (variant?.id !== lastVariantId && variant?.media_id) this.showMedia(panel, variant.media_id);
        lastVariantId = variant?.id || null;
      };

      this.checkoutReturnCallbacks.push(() => {
        // A restored document retains the loading state from its checkout redirect.
        if (!awaitingCheckoutReturn) return;
        awaitingCheckoutReturn = false;
        buyPending = false;
        render();
      });

      optionGroups.forEach(group => group.addEventListener('change', render));
      panel.querySelectorAll('[data-tbsp-plans] input[type="radio"]').forEach(input => input.addEventListener('change', render));
      minus?.addEventListener('click', () => {
        normalizeQuantity(findVariant());
        quantity.stepDown();
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      });
      plus?.addEventListener('click', () => {
        normalizeQuantity(findVariant());
        quantity.stepUp();
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      });
      quantity.addEventListener('input', () => {
        // Let customers clear the field while typing; commit invalid/empty values on change or submit.
        if (quantity.value !== '' && !quantity.validity.badInput) render();
      });
      quantity.addEventListener('change', render);

      const media = [...panel.querySelectorAll('[data-tbsp-media]')];
      panel.querySelectorAll('[data-tbsp-thumb]').forEach(thumb => thumb.addEventListener('click', () => this.showMedia(panel, thumb.dataset.tbspThumb)));
      const moveMedia = direction => {
        const index = media.findIndex(item => !item.hidden);
        this.showMedia(panel, media[(index + direction + media.length) % media.length]?.dataset.tbspMedia);
      };
      panel.querySelector('[data-tbsp-media-prev]')?.addEventListener('click', () => moveMedia(-1));
      panel.querySelector('[data-tbsp-media-next]')?.addEventListener('click', () => moveMedia(1));

      form.addEventListener('submit', async event => {
        event.preventDefault();
        render();
        if (event.submitter && event.submitter !== add || !canBuy || addPending || buyPending || cartState()?.loading) return;
        addPending = true;
        syncButtons();
        try {
          const state = cartState();
          if (!state?.addToCart) throw new Error(errorText?.dataset.fallback || 'Could not add this item to cart.');
          await state.addToCart(event);
        } catch (error) {
          showError(error.message || errorText?.dataset.fallback || 'Could not add this item to cart.');
        } finally {
          addPending = false;
          syncButtons();
        }
      });
      buy?.addEventListener('click', async () => {
        render();
        if (!canBuy || addPending || buyPending || cartState()?.loading) return;
        buyPending = true;
        syncButtons();
        const state = cartState();
        if (state) state.errorMessage = false;
        if (errorText) errorText.textContent = '';
        let releaseCart;
        try {
          if (window.mwCart?.acquire) releaseCart = await window.mwCart.acquire();
          render();
          if (!canBuy) throw new Error('This purchase option is unavailable.');
          const item = { id: Number(panel.querySelector('[data-tbsp-variant-id]').value), quantity: Number(quantity.value) };
          if (planInput && !planInput.disabled && planInput.value) item.selling_plan = Number(planInput.value);
          const root = window.Shopify?.routes?.root || '/';
          const routeRoot = root.endsWith('/') ? root : `${root}/`;
          const response = await fetch(`${routeRoot}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] })
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(result.description || result.message || errorText?.dataset.fallback || 'Could not add this item to cart.');
          awaitingCheckoutReturn = true;
          this.goToCheckout(`${routeRoot}checkout`);
        } catch (error) {
          awaitingCheckoutReturn = false;
          showError(error.message || errorText?.dataset.fallback || 'Could not start checkout.');
        } finally {
          if (typeof releaseCart === 'function') releaseCart();
          if (!awaitingCheckoutReturn) {
            buyPending = false;
            syncButtons();
          }
        }
      });
      render();
    }
  }

  window.TabbedBestSellingProduct = TabbedBestSellingProduct;
  const instances = new WeakMap();
  const mount = scope => scope.querySelectorAll('[data-tbsp-section]').forEach(root => {
    if (root.dataset.tbspMounted) return;
    root.dataset.tbspMounted = 'true';
    instances.set(root, new TabbedBestSellingProduct(root));
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-tbsp-section]').forEach(root => {
      instances.get(root)?.restoreCheckoutButtons();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document));
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:block:select', event => {
    const tab = event.target.closest('[role="tab"]');
    if (tab) tab.click();
  });
})();
